import React, { useState } from 'react';
import { Input, Option, Select, Spinner } from '@material-tailwind/react';
import { PlusIcon, Trash2Icon, DownloadIcon, UploadIcon, CheckCircle2Icon, AlertTriangleIcon, RefreshCcwIcon, UsersIcon, DatabaseIcon } from 'lucide-react';
import {
    useGetInventoryConsumers, useAddInventoryConsumer, useDeleteInventoryConsumer,
    useGetInventoryItems, useDryRunInventoryCsvImport, useApplyInventoryCsvImport,
} from '../../custom-hooks/use-inventory';
import { GetInventoryCsvTemplate, ExportAllInventoryItemsCsv, ExportAllInventoryTransactionsCsv } from '../../services/backend';
import { downloadCsvResponse } from './utils';
import { Notify, NotifyError } from '../../utils/functions';
import { useConfirm } from '../../custom-hooks/confirm-dialog';

const ConsumersCard = () => {
    const [newName, setNewName] = useState('');
    const { data: consumers = [], isLoading } = useGetInventoryConsumers();
    const addMutation = useAddInventoryConsumer();
    const deleteMutation = useDeleteInventoryConsumer();
    const { confirm, ConfirmationDialog } = useConfirm();

    const handleAdd = async (e) => {
        e.preventDefault();
        if (!newName.trim()) return;
        await addMutation.mutateAsync({ name: newName.trim() });
        setNewName('');
    };

    const handleRemove = async (consumer) => {
        const ok = await confirm({
            title: 'Remove Person',
            message: `Remove "${consumer.name}" from the list? They'll no longer appear when logging entries.`,
            confirmText: 'Remove',
            confirmBtnColor: 'red',
        });
        if (ok) await deleteMutation.mutateAsync(consumer._id);
    };

    return (
        <div className="rounded-2xl border border-inv-cream-200 bg-white p-5">
            <div className="mb-1 flex items-center gap-2">
                <UsersIcon size={18} className="text-inv-brand" />
                <h3 className="font-bold text-inv-ink">People</h3>
            </div>
            <p className="mb-4 text-xs text-[#8C8175]">The managed list of staff who can be selected as "who" when logging entries — replaces free-typed names.</p>

            <form onSubmit={handleAdd} className="mb-4 flex gap-2">
                <Input label="Add a person" value={newName} onChange={(e) => setNewName(e.target.value)} />
                <button
                    type="submit"
                    disabled={!newName.trim() || addMutation.isLoading}
                    className="flex shrink-0 items-center gap-1.5 rounded-xl bg-inv-brand px-3.5 text-sm font-semibold text-white hover:bg-inv-brand-dark disabled:opacity-50"
                >
                    <PlusIcon size={15} /> Add
                </button>
            </form>

            {isLoading ? (
                <div className="flex justify-center py-6"><Spinner className="h-5 w-5 text-inv-brand" /></div>
            ) : consumers.length === 0 ? (
                <p className="text-sm text-[#8C8175]">No one added yet.</p>
            ) : (
                <ul className="flex flex-wrap gap-2">
                    {consumers.map((c) => (
                        <li key={c._id} className="flex items-center gap-1.5 rounded-full bg-inv-cream-100 py-1 pl-3 pr-1.5 text-sm text-inv-ink">
                            {c.name}
                            <button onClick={() => handleRemove(c)} className="flex h-5 w-5 items-center justify-center rounded-full text-[#8C8175] hover:bg-white hover:text-red-600">
                                <Trash2Icon size={11} />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
            <ConfirmationDialog />
        </div>
    );
};

const ExportDataCard = () => {
    const [busyKey, setBusyKey] = useState(null);

    const handleExport = async (key, fetchFn, fileName) => {
        setBusyKey(key);
        try {
            const response = await fetchFn();
            downloadCsvResponse(response, fileName);
            Notify('Export downloaded successfully', 'success');
        } catch (error) {
            NotifyError('Failed to export data', error);
        } finally {
            setBusyKey(null);
        }
    };

    return (
        <div className="rounded-2xl border border-inv-cream-200 bg-white p-5">
            <div className="mb-1 flex items-center gap-2">
                <DatabaseIcon size={18} className="text-inv-brand" />
                <h3 className="font-bold text-inv-ink">Export Data</h3>
            </div>
            <p className="mb-4 text-xs text-[#8C8175]">Download the full system data as CSV — everything, not just one item.</p>

            <div className="flex flex-col gap-2 sm:flex-row">
                <button
                    onClick={() => handleExport('items', ExportAllInventoryItemsCsv, 'inventory_items.csv')}
                    disabled={busyKey === 'items'}
                    className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-inv-cream-200 px-3.5 py-2.5 text-sm font-semibold text-inv-ink hover:bg-inv-cream-100 disabled:opacity-50"
                >
                    {busyKey === 'items' ? <Spinner className="h-4 w-4" /> : <DownloadIcon size={14} />} All Items
                </button>
                <button
                    onClick={() => handleExport('transactions', ExportAllInventoryTransactionsCsv, 'inventory_all_transactions.csv')}
                    disabled={busyKey === 'transactions'}
                    className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-inv-cream-200 px-3.5 py-2.5 text-sm font-semibold text-inv-ink hover:bg-inv-cream-100 disabled:opacity-50"
                >
                    {busyKey === 'transactions' ? <Spinner className="h-4 w-4" /> : <DownloadIcon size={14} />} All Transactions
                </button>
            </div>
        </div>
    );
};

const CsvImportCard = () => {
    const [itemId, setItemId] = useState('');
    const [fileName, setFileName] = useState('');
    const [csvContent, setCsvContent] = useState('');
    const [validation, setValidation] = useState(null);
    const [confirmText, setConfirmText] = useState('');
    const [templateBusy, setTemplateBusy] = useState(false);

    const { data: items = [] } = useGetInventoryItems();
    const dryRunMutation = useDryRunInventoryCsvImport();
    const applyMutation = useApplyInventoryCsvImport();

    const handleDownloadTemplate = async () => {
        setTemplateBusy(true);
        try {
            const response = await GetInventoryCsvTemplate();
            downloadCsvResponse(response, 'inventory_import_template.csv');
        } catch (error) {
            NotifyError('Failed to download template', error);
        } finally {
            setTemplateBusy(false);
        }
    };

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setFileName(file.name);
        setCsvContent(await file.text());
        setValidation(null);
        setConfirmText('');
    };

    const handleDryRun = async () => {
        if (!itemId || !csvContent) {
            Notify('Select an item and a CSV file first', 'warn');
            return;
        }
        const response = await dryRunMutation.mutateAsync({ itemId, csvContent });
        setValidation(response?.data || null);
        setConfirmText('');
    };

    const handleApply = async () => {
        if (confirmText.trim() !== 'APPLY') {
            Notify('Type APPLY to confirm', 'warn');
            return;
        }
        const response = await applyMutation.mutateAsync({ itemId, csvContent });
        setValidation((prev) => ({ ...(prev || {}), applied: response?.data || null }));
    };

    return (
        <div className="rounded-2xl border border-inv-cream-200 bg-white p-5">
            <h3 className="mb-1 font-bold text-inv-ink">Historical CSV Import</h3>
            <p className="mb-4 text-xs text-[#8C8175]">Migrate one item's history at a time — same layout as the old spreadsheet sheet (Date, Time, Description, IN, OUT, Notes).</p>

            <button
                onClick={handleDownloadTemplate}
                disabled={templateBusy}
                className="mb-4 flex items-center gap-1.5 rounded-xl border border-inv-cream-200 px-3.5 py-2 text-sm font-semibold text-inv-ink hover:bg-inv-cream-100 disabled:opacity-50"
            >
                <DownloadIcon size={14} /> Download Template
            </button>

            <div className="space-y-3">
                {/* `selected` computes the closed-box label ourselves — MT's own display
                    lookup is unreliable for options backed by async-loaded data */}
                <Select
                    label="Target Item"
                    value={itemId}
                    onChange={setItemId}
                    selected={() => items.find((i) => i._id === itemId)?.name || ''}
                >
                    {items.map((i) => <Option key={i._id} value={i._id}>{i.name}</Option>)}
                </Select>

                <div className="rounded-xl border border-dashed border-inv-cream-200 p-3">
                    <input type="file" accept=".csv,text/csv" onChange={handleFileChange} className="block w-full text-sm" />
                    {fileName && <p className="mt-1.5 text-xs text-[#8C8175]">Selected: {fileName}</p>}
                </div>

                <button
                    onClick={handleDryRun}
                    disabled={!itemId || !csvContent || dryRunMutation.isLoading}
                    className="flex items-center gap-1.5 rounded-xl bg-inv-ink px-3.5 py-2 text-sm font-semibold text-white disabled:opacity-50"
                >
                    {dryRunMutation.isLoading ? <Spinner className="h-4 w-4" /> : <RefreshCcwIcon size={14} />} Validate (Dry Run)
                </button>

                {validation && (
                    <div className={`rounded-xl border p-3.5 ${validation.isValid ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'}`}>
                        <div className="flex items-center gap-2">
                            {validation.isValid ? <CheckCircle2Icon size={16} className="text-emerald-600" /> : <AlertTriangleIcon size={16} className="text-red-600" />}
                            <span className={`text-sm font-semibold ${validation.isValid ? 'text-emerald-700' : 'text-red-700'}`}>
                                {validation.isValid ? 'Validation passed' : 'Validation failed'}
                            </span>
                        </div>
                        {validation.summary && (
                            <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                                <div className="rounded-lg bg-white px-2 py-1.5">Rows: {validation.summary.totalRows}</div>
                                <div className="rounded-lg bg-white px-2 py-1.5">Valid: {validation.summary.validRows}</div>
                                <div className="rounded-lg bg-white px-2 py-1.5">Projected balance: {validation.summary.projectedRemaining}</div>
                            </div>
                        )}
                        {validation.errors?.length > 0 && (
                            <ul className="mt-2 max-h-32 space-y-1 overflow-y-auto text-xs text-red-700">
                                {validation.errors.slice(0, 20).map((err, i) => <li key={i}>Line {err.line}: {err.message}</li>)}
                            </ul>
                        )}

                        {validation.isValid && !validation.applied && (
                            <div className="mt-3 flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
                                <div className="flex-1">
                                    <Input
                                        label='Type "APPLY" to confirm'
                                        value={confirmText}
                                        onChange={(e) => setConfirmText(e.target.value)}
                                    />
                                </div>
                                <button
                                    onClick={handleApply}
                                    disabled={confirmText.trim() !== 'APPLY' || applyMutation.isLoading}
                                    className="flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-xl bg-inv-brand px-3.5 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
                                >
                                    <UploadIcon size={14} /> Apply Import
                                </button>
                            </div>
                        )}
                        {validation.applied && (
                            <p className="mt-2 text-sm font-semibold text-emerald-700">
                                Imported {validation.applied.imported} rows. Final balance: {validation.applied.finalRemaining}.
                            </p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

const ManageView = () => (
    <div className="space-y-5">
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            <ConsumersCard />
            <ExportDataCard />
        </div>
        <CsvImportCard />
    </div>
);

export default ManageView;
